import {
  CommonHelperFunctions,
  DashboardState,
  EProjectType,
  ESdChangeReason,
  IFillStyle,
  IKuikaStyle,
  IStylingBorder,
  IStylingEffects,
  IStylingPosition,
  IStylingShadow,
  IStylingTypography,
  StyleState
} from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { CustomTagProps } from "rc-select/lib/BaseSelect";
import React from "react";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { IPureDesignComponentState, PureSdComponent } from "../../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { DeleteModal } from "../../../../common/delete-modal";
import KuikaDropdownMenu, {
  KuikaDropdownMenuItemType
} from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaLoader from "../../../../ui-kit/kuika-loader/kuika-loader";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs, { KuikaTabOption } from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaSmallMediumText from "../../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import { ActionSectionAddButton } from "../../properties-panel/action-section-header";
import ResetChangesPopover from "../reset-changes-popover/reset-changes-popover";
import { StylingTag } from "../styling-selector/styling-tag";
import {
  hasGenuineStateOverride,
  type IStyleTagCollections,
  pickEffectsBaseProps,
  resolveCleanBaseSource,
  resolveTagSaveTarget,
  sectionHasBaseStyle,
  STATE_ALLOWED_PROPS,
  styleObjHasBaseStyle,
  type TagSaveTarget
} from "../styling-window-helpers";
import {
  applyStyleTagIds,
  buildUnifiedStyleList,
  getAppliedStyleTagIds,
  IUnifiedStyleItem,
  removeStyleTagIds
} from "./style-tag-helpers";
import "./unified-styling-selector.scss";

export enum EditMode {
  custom = 1,
  edit = 2,
  create = 3
}

export type { IUnifiedStyleItem, PanelType } from "./style-tag-helpers";

interface IDeleteArguments {
  isDeleteStyleModalVisible: boolean;
  deletingStyleItem: IUnifiedStyleItem | null;
  performStyleTagOnClose: (() => void) | undefined;
}

export interface IUnifiedStylingSelectorProps {
  form: FormInstance<IKuikaStyle>;
  onEditModeChange?: (isEditing: boolean, groupId: string | null, tagId?: string) => void;
  editingGroupId: string | null;
  selectedState: StyleState;
  onStateChange: (state: StyleState, unifiedStyleId?: string) => void;
  onStateReset?: () => void;
  onResetState?: (state: StyleState) => void;
  pendingTagStateStyles?: IKuikaStyle | null;
  // KOA-15838: clean base snapshot captured by styling-window when leaving the
  // none state. While editing a tag in a non-none state, the user's None/base
  // edits live only here (not in pendingTagStateStyles, which holds states) —
  // saveUnifiedStyle reads it to persist base edits instead of dropping them.
  statePreviewBackup?: IKuikaStyle | null;
}

interface IUnifiedStylingSelectorState {
  searchText: string;
  selectedIds: string[];
  mode: EditMode;
  editingTagValue: string | null;
  formBackup: IKuikaStyle | null;
  isDropdownVisible: boolean;
  activeStylingMenuTag: string | null;
  deleteArguments: IDeleteArguments;
  isSaving: boolean;
  // Creating a tag lands in the same edit mode as "Edit style"; this is what lets the field say
  // which of the two the user is in.
  isEditingNewTag: boolean;
  // The name of the tag being created, from "Add" until its chip enters edit mode. The project
  // round trip takes seconds, and the field shows a loader for that time.
  creatingStyleName: string | null;
}

/**
 * The id rc-select gives the Style field's search box. The "Style" caption is its `<label>`, and
 * the "+" beside the chips puts the caret there.
 */
const STYLE_SEARCH_INPUT_ID: string = "kuika-styling-tag-search";

interface IStyleStateTab {
  state: StyleState;
  label: string;
}

const STYLE_STATE_TABS: readonly IStyleStateTab[] = [
  { state: "none", label: "None" },
  { state: "hover", label: "Hover" },
  { state: "pressed", label: "Pressed" },
  { state: "focused", label: "Focused" },
  { state: "disabled", label: "Disabled" }
];

/** A single project-level styling entry from any of the styling collections. */
type StyleSectionEntry =
  | IStylingPosition
  | IStylingTypography
  | IFillStyle
  | IStylingBorder
  | IStylingShadow
  | IStylingEffects;

/** A styling section entry paired with the section it belongs to. */
interface EditingTagSectionEntry {
  section: keyof IKuikaStyle;
  entry: StyleSectionEntry;
}

class UnifiedStylingSelector extends PureSdComponent<
  IUnifiedStylingSelectorProps & IWithSDProps & IWithContextProp,
  IUnifiedStylingSelectorState & IPureDesignComponentState
> {
  constructor(props: IUnifiedStylingSelectorProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      searchText: "",
      selectedIds: [],
      mode: EditMode.custom,
      editingTagValue: null,
      formBackup: null,
      isDropdownVisible: false,
      activeStylingMenuTag: null,
      deleteArguments: {
        isDeleteStyleModalVisible: false,
        deletingStyleItem: null,
        performStyleTagOnClose: undefined
      },
      isSaving: false,
      isEditingNewTag: false,
      creatingStyleName: null
    };
  }

  // A create in flight, set before its first await. Enter, the "Add" row and rc-select's own typed
  // row can all ask for the same create in one event, before any setState has landed. Without this
  // each of them, and every repeat press during the round trip, would create another tag.
  private isCreatingStyle: boolean = false;

  componentDidUpdate(
    prevProps: IUnifiedStylingSelectorProps & IWithSDProps & IWithContextProp,
    prevState: IUnifiedStylingSelectorState & IPureDesignComponentState
  ) {
    const isSearching: boolean = this.state.isDropdownVisible && this.state.mode !== EditMode.edit;
    const searchMoved: boolean = !prevState.isDropdownVisible || prevState.searchText !== this.state.searchText;
    if (isSearching && searchMoved) {
      this.revealStyleSearch();
    }
    // The reveal parked the rows at their end. Once the search closes they go back to the first
    // row, where a component's tags start.
    if (prevState.isDropdownVisible && !this.state.isDropdownVisible) {
      this.resetStyleRowsScroll();
    }

    if (this.props.sd.selectedKuikaIds !== prevProps.sd.selectedKuikaIds) {
      // Selection changed while editing a tag. Clear ONLY this selector's local
      // edit state — the parent StylingWindow.componentDidUpdate owns the full
      // teardown (discard the unsaved edit by restoring the previous DI from
      // editDiBackup, reset selectedState to "none", and reload the new DI).
      // Do NOT call onEditModeChange here: the parent already restores the DI,
      // so a second call would double-commit the restore.
      if (this.state.mode === EditMode.edit) {
        this.setState({ mode: EditMode.custom, editingTagValue: null, formBackup: null, isEditingNewTag: false });
      }
    }
  }

  // The chip row. It wraps the Form.Item that renders the select, so it outlives a form reset.
  private tagsRowRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

  // Past three rows the chips scroll, and the search box always comes after the last chip. It can
  // sit below the visible rows, so the row is scrolled to its end whenever a search opens or its
  // text changes (a longer name can wrap the box onto a new row).
  revealStyleSearch = (): void => {
    const selector: HTMLElement | null | undefined =
      this.tagsRowRef.current?.querySelector<HTMLElement>(".rc-select-selector");
    if (selector) {
      selector.scrollTop = selector.scrollHeight;
    }
  };

  resetStyleRowsScroll = (): void => {
    const selector: HTMLElement | null | undefined =
      this.tagsRowRef.current?.querySelector<HTMLElement>(".rc-select-selector");
    if (selector) {
      selector.scrollTop = 0;
    }
  };

  // Aggregate all styles from all panels and group by groupId
  getUnifiedStyleList = (): IUnifiedStyleItem[] => buildUnifiedStyleList(this.props.sd.project);

  // Get currently selected unified style IDs from form
  getSelectedStyleIds = (): string[] =>
    getAppliedStyleTagIds(this.props.form.getFieldsValue() as IKuikaStyle, this.getUnifiedStyleList());

  // Create a new unified style with entries in all 5 storages
  createUnifiedStyle = async (name: string) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    const project = _.cloneDeep(this.props.sd.project);
    const groupId = Guid.create().toString();
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    const componentType = selectedDi?.item?.name;

    // Create Position style
    const positionStyle: IStylingPosition = {
      positionId: Guid.create().toString(),
      positionIds: null,
      name,
      groupId,
      styleType: "custom",
      componentType,
      kuikaid: this.props.sd.selectedKuikaIds[0],
      isMarginLocked: fieldsValue.position?.isMarginLocked,
      isPaddingLocked: fieldsValue.position?.isPaddingLocked,
      marginTop: fieldsValue.position?.marginTop,
      marginRight: fieldsValue.position?.marginRight,
      marginBottom: fieldsValue.position?.marginBottom,
      marginLeft: fieldsValue.position?.marginLeft,
      paddingTop: fieldsValue.position?.paddingTop,
      paddingRight: fieldsValue.position?.paddingRight,
      paddingBottom: fieldsValue.position?.paddingBottom,
      paddingLeft: fieldsValue.position?.paddingLeft,
      width: fieldsValue.position?.width,
      height: fieldsValue.position?.height,
      widthStyle: fieldsValue.position?.widthStyle,
      heightStyle: fieldsValue.position?.heightStyle,
      maxWidth: fieldsValue.position?.maxWidth,
      maxHeight: fieldsValue.position?.maxHeight,
      maxWidthStyle: fieldsValue.position?.maxWidthStyle,
      maxHeightStyle: fieldsValue.position?.maxHeightStyle,
      minWidth: fieldsValue.position?.minWidth,
      minHeight: fieldsValue.position?.minHeight,
      minWidthStyle: fieldsValue.position?.minWidthStyle,
      minHeightStyle: fieldsValue.position?.minHeightStyle,
      alignItems: fieldsValue.position?.alignItems,
      textAlign: fieldsValue.position?.textAlign,
      display: fieldsValue.position?.display,
      xAxis: fieldsValue.position?.xAxis,
      yAxis: fieldsValue.position?.yAxis
    };

    // Create Typography style
    const typoStyle: IStylingTypography = {
      typoId: Guid.create().toString(),
      typoIds: null,
      name,
      groupId,
      styleType: "custom",
      componentType,
      kuikaid: this.props.sd.selectedKuikaIds[0],
      fontFamily: fieldsValue.text?.fontFamily,
      fontWeight: fieldsValue.text?.fontWeight,
      fontStyle: fieldsValue.text?.fontStyle,
      fontSize: fieldsValue.text?.fontSize,
      fontSizeStyle: fieldsValue.text?.fontSizeStyle,
      color: fieldsValue.text?.color,
      lineHeight: fieldsValue.text?.lineHeight,
      lineHeightStyle: fieldsValue.text?.lineHeightStyle,
      letterSpacing: fieldsValue.text?.letterSpacing,
      letterSpacingStyle: fieldsValue.text?.letterSpacingStyle,
      textDecoration: fieldsValue.text?.textDecoration,
      textTransform: fieldsValue.text?.textTransform,
      dontAffectedMobileSystem: fieldsValue.text?.dontAffectedMobileSystem
    };

    // Create Fill style
    const fillStyle: IFillStyle = {
      fillId: Guid.create().toString(),
      fillIds: null,
      name,
      groupId,
      styleType: "custom",
      componentType,
      kuikaid: this.props.sd.selectedKuikaIds[0],
      isVisible: fieldsValue.fill?.isVisible,
      color: fieldsValue.fill?.color,
      backgroundImage: fieldsValue.fill?.backgroundImage,
      repeatSwitch: fieldsValue.fill?.repeatSwitch,
      bgImageSize: fieldsValue.fill?.bgImageSize,
      stretch: fieldsValue.fill?.stretch,
      imageFit: fieldsValue.fill?.imageFit,
      bgImagePosition: fieldsValue.fill?.bgImagePosition
    };

    // Create Border style
    const borderStyle: IStylingBorder = {
      borderId: Guid.create().toString(),
      borderIds: null,
      name,
      groupId,
      styleType: "custom",
      componentType,
      kuikaid: this.props.sd.selectedKuikaIds[0],
      isVisible: fieldsValue.border?.isVisible,
      borderColor: fieldsValue.border?.borderColor,
      isBorderWidthLocked: fieldsValue.border?.isBorderWidthLocked,
      borderTopWidth: fieldsValue.border?.borderTopWidth,
      borderRightWidth: fieldsValue.border?.borderRightWidth,
      borderBottomWidth: fieldsValue.border?.borderBottomWidth,
      borderLeftWidth: fieldsValue.border?.borderLeftWidth,
      isBorderRadiusLocked: fieldsValue.border?.isBorderRadiusLocked,
      borderTopLeftRadius: fieldsValue.border?.borderTopLeftRadius,
      borderTopRightRadius: fieldsValue.border?.borderTopRightRadius,
      borderBottomRightRadius: fieldsValue.border?.borderBottomRightRadius,
      borderBottomLeftRadius: fieldsValue.border?.borderBottomLeftRadius,
      borderStyle: fieldsValue.border?.borderStyle
    };

    // Create Shadow style
    const shadowStyle: IStylingShadow = {
      shadowId: Guid.create().toString(),
      shadowIds: null,
      name,
      groupId,
      styleType: "custom",
      componentType,
      kuikaid: this.props.sd.selectedKuikaIds[0],
      isVisible: fieldsValue.shadow?.isVisible,
      color: fieldsValue.shadow?.color,
      x: fieldsValue.shadow?.x,
      y: fieldsValue.shadow?.y,
      b: fieldsValue.shadow?.b,
      s: fieldsValue.shadow?.s
    };

    // Create Effects style. KOA-18159: snapshot every base effect field, not just
    // isVisible/opacity.
    const effectsStyle: IStylingEffects = {
      effectsId: Guid.create().toString(),
      effectsIds: null,
      name,
      groupId,
      styleType: "custom",
      componentType,
      kuikaid: this.props.sd.selectedKuikaIds[0],
      ...(fieldsValue.effects ? pickEffectsBaseProps(fieldsValue.effects) : {})
    };

    // Add to project
    if (!project.stylingPosition) project.stylingPosition = [];
    if (!project.stylingTypography) project.stylingTypography = [];
    if (!project.stylingFill) project.stylingFill = [];
    if (!project.stylingBorder) project.stylingBorder = [];
    if (!project.stylingShadow) project.stylingShadow = [];
    if (!project.stylingEffects) project.stylingEffects = [];

    project.stylingPosition.push(positionStyle);
    project.stylingTypography.push(typoStyle);
    project.stylingFill.push(fillStyle);
    project.stylingBorder.push(borderStyle);
    project.stylingShadow.push(shadowStyle);
    project.stylingEffects.push(effectsStyle);

    await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);

    // Update form with new style IDs
    const updatedFieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (updatedFieldsValue.position?.positionIds) {
      updatedFieldsValue.position.positionIds.push(positionStyle.positionId!);
    } else if (updatedFieldsValue.position) {
      updatedFieldsValue.position.positionIds = [positionStyle.positionId!];
    }
    if (updatedFieldsValue.text?.typoIds) {
      updatedFieldsValue.text.typoIds.push(typoStyle.typoId!);
    } else if (updatedFieldsValue.text) {
      updatedFieldsValue.text.typoIds = [typoStyle.typoId!];
    }
    if (updatedFieldsValue.fill?.fillIds) {
      updatedFieldsValue.fill.fillIds.push(fillStyle.fillId!);
    } else if (updatedFieldsValue.fill) {
      updatedFieldsValue.fill.fillIds = [fillStyle.fillId!];
    }
    if (updatedFieldsValue.border?.borderIds) {
      updatedFieldsValue.border.borderIds.push(borderStyle.borderId!);
    } else if (updatedFieldsValue.border) {
      updatedFieldsValue.border.borderIds = [borderStyle.borderId!];
    }
    if (updatedFieldsValue.shadow?.shadowIds) {
      updatedFieldsValue.shadow.shadowIds.push(shadowStyle.shadowId!);
    } else if (updatedFieldsValue.shadow) {
      updatedFieldsValue.shadow.shadowIds = [shadowStyle.shadowId!];
    }
    // KOA-18159: `getFieldsValue()` returns REGISTERED fields only, so a collapsed
    // Effects accordion left the new tag's effects entry unlinked. Create it here.
    if (!updatedFieldsValue.effects) {
      updatedFieldsValue.effects = { effectsId: null } as IStylingEffects;
    }
    if (updatedFieldsValue.effects.effectsIds) {
      updatedFieldsValue.effects.effectsIds.push(effectsStyle.effectsId!);
    } else {
      updatedFieldsValue.effects.effectsIds = [effectsStyle.effectsId!];
    }

    // Set unified group info
    updatedFieldsValue.styleGroupId = groupId;
    updatedFieldsValue.styleGroupName = name;

    this.props.form.setFieldsValue(updatedFieldsValue);
    this.props.form.submit();

    return groupId;
  };

  // Edit a unified style (enter edit mode for all panels)
  editUnifiedStyle = (styleItem: IUnifiedStyleItem, isNewTag: boolean = false) => {
    // Backup current form values for cancel restore
    const currentFieldsValue = _.cloneDeep(this.props.form.getFieldsValue());
    this.setState({
      mode: EditMode.edit,
      editingTagValue: styleItem.id,
      formBackup: currentFieldsValue,
      isEditingNewTag: isNewTag
    });

    // For legacy styles, pass the panel type; for unified styles, pass "unified" to indicate all panels
    const editingId = styleItem.isLegacy ? styleItem.panelType?.toLowerCase() || null : "unified";
    this.props.onEditModeChange?.(true, editingId, styleItem.id);

    // KOA-15838: when a state tab (hover/pressed/focused/disabled) is already
    // active, this base-only form load would show the tag's BASE instead of its
    // {state}Styles. The parent's handleEditModeChange replays the tag load
    // through handleStateChange for the active state and owns the form write in
    // that case, so skip the base load + submit here to avoid a redundant DI
    // write in the wrong (state) context.
    if (this.props.selectedState !== "none") {
      return;
    }

    // Set form values from the style, preserving existing IDs arrays
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();

    if (styleItem.styles.position) {
      // Preserve existing positionIds
      const existingPositionIds = fieldsValue.position?.positionIds;
      const {
        positionIds: _pIds,
        positionId: _pId,
        hoverStyles: _h,
        pressedStyles: _p,
        focusedStyles: _f,
        disabledStyles: _d,
        name: _n,
        groupId: _g,
        styleType: _st,
        componentType: _ct,
        kuikaid: _k,
        ...positionCssProps
      } = styleItem.styles.position as any;
      Object.assign(fieldsValue.position || {}, positionCssProps);
      if (fieldsValue.position) {
        fieldsValue.position.positionId = styleItem.styles.position.positionId;
        fieldsValue.position.positionIds = existingPositionIds ?? null;
      }
    }
    if (styleItem.styles.text) {
      // Preserve existing typoIds
      const existingTypoIds = fieldsValue.text?.typoIds;
      const {
        typoIds: _tIds,
        typoId: _tId,
        hoverStyles: _h,
        pressedStyles: _p,
        focusedStyles: _f,
        disabledStyles: _d,
        name: _n,
        groupId: _g,
        styleType: _st,
        componentType: _ct,
        kuikaid: _k,
        ...textCssProps
      } = styleItem.styles.text as any;
      Object.assign(fieldsValue.text || {}, textCssProps);
      if (fieldsValue.text) {
        fieldsValue.text.typoId = styleItem.styles.text.typoId;
        fieldsValue.text.typoIds = existingTypoIds ?? null;
      }
    }
    if (styleItem.styles.fill) {
      // Preserve existing fillIds
      const existingFillIds = fieldsValue.fill?.fillIds;
      const {
        fillIds: _fIds,
        fillId: _fId,
        hoverStyles: _h,
        pressedStyles: _p,
        focusedStyles: _f,
        disabledStyles: _d,
        name: _n,
        groupId: _g,
        styleType: _st,
        componentType: _ct,
        kuikaid: _k,
        ...fillCssProps
      } = styleItem.styles.fill as any;
      Object.assign(fieldsValue.fill || {}, fillCssProps);
      if (fieldsValue.fill) {
        fieldsValue.fill.fillId = styleItem.styles.fill.fillId;
        fieldsValue.fill.fillIds = existingFillIds ?? null;
      }
    }
    if (styleItem.styles.border) {
      // Preserve existing borderIds
      const existingBorderIds = fieldsValue.border?.borderIds;
      const {
        borderIds: _bIds,
        borderId: _bId,
        hoverStyles: _h,
        pressedStyles: _p,
        focusedStyles: _f,
        disabledStyles: _d,
        name: _n,
        groupId: _g,
        styleType: _st,
        componentType: _ct,
        kuikaid: _k,
        ...borderCssProps
      } = styleItem.styles.border as any;
      Object.assign(fieldsValue.border || {}, borderCssProps);
      if (fieldsValue.border) {
        fieldsValue.border.borderId = styleItem.styles.border.borderId;
        fieldsValue.border.borderIds = existingBorderIds ?? null;
      }
    }
    if (styleItem.styles.shadow) {
      // Preserve existing shadowIds
      const existingShadowIds = fieldsValue.shadow?.shadowIds;
      const {
        shadowIds: _sIds,
        shadowId: _sId,
        hoverStyles: _h,
        pressedStyles: _p,
        focusedStyles: _f,
        disabledStyles: _d,
        name: _n,
        groupId: _g,
        styleType: _st,
        componentType: _ct,
        kuikaid: _k,
        ...shadowCssProps
      } = styleItem.styles.shadow as any;
      Object.assign(fieldsValue.shadow || {}, shadowCssProps);
      if (fieldsValue.shadow) {
        fieldsValue.shadow.shadowId = styleItem.styles.shadow.shadowId;
        fieldsValue.shadow.shadowIds = existingShadowIds ?? null;
      }
    }
    if (styleItem.styles.effects) {
      // KOA-18159: skipped before, so tag edit showed the COMPONENT's effects and
      // the next save wrote them onto the tag.
      const existingEffectsIds = fieldsValue.effects?.effectsIds;
      const {
        effectsIds: _eIds,
        effectsId: _eId,
        hoverStyles: _h,
        pressedStyles: _p,
        focusedStyles: _f,
        disabledStyles: _d,
        name: _n,
        groupId: _g,
        styleType: _st,
        componentType: _ct,
        kuikaid: _k,
        ...effectsCssProps
      } = styleItem.styles.effects as any;
      if (!fieldsValue.effects) fieldsValue.effects = { effectsId: null } as IStylingEffects;
      Object.assign(fieldsValue.effects, effectsCssProps);
      fieldsValue.effects.effectsId = styleItem.styles.effects.effectsId;
      fieldsValue.effects.effectsIds = existingEffectsIds ?? null;
    }

    fieldsValue.styleGroupId = styleItem.groupId;
    fieldsValue.styleGroupName = styleItem.displayName;

    this.props.form.setFieldsValue(fieldsValue);
    this.props.form.submit();
  };

  // Save unified style changes
  saveUnifiedStyle = async (newName?: string) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    const project = _.cloneDeep(this.props.sd.project);
    const groupId = fieldsValue.styleGroupId;

    const name = newName || fieldsValue.styleGroupName || "";

    const isInState = this.props.selectedState !== "none";
    // KOA-15838: a single-panel (legacy) tag is edited with editingGroupId set to
    // its panel type ("layout"/"text"/"fill"/"border"/"shadow"/"effects"); a
    // unified tag uses "unified". This is the reliable discriminator — `groupId`
    // (= form.styleGroupId) is unreliable for legacy tags because a state switch
    // sets it to the section id, which then mis-routes the save into the
    // groupId-matched unified branch (where it silently matches nothing).
    const editingGroupId: string | null = this.props.editingGroupId;
    // KOA-18159: a unified-tag save with no groupId must write NOTHING — it used to
    // fall through to the legacy branch and overwrite a shared tag (`Button_Fill`).
    const saveTarget: TagSaveTarget = resolveTagSaveTarget(editingGroupId, groupId);
    if (saveTarget === "none") {
      KMainFunctions.displayWarningNotification(
        "The edited style could not be identified. Please reopen the style and try again."
      );
      return;
    }

    // Build effective pending: start from accumulated pending, then flush current form for the active state
    const effectivePending: IKuikaStyle = this.props.pendingTagStateStyles
      ? _.cloneDeep(this.props.pendingTagStateStyles)
      : ({} as IKuikaStyle);

    if (isInState) {
      const stateProp = `${this.props.selectedState}Styles`;
      const sections: (keyof IKuikaStyle)[] = ["position", "text", "fill", "border", "shadow", "effects"];
      // Only CSS-relevant properties reach a state bucket. KOA-18159: a local copy
      // of this allow-list had drifted to `effects: ["opacity"]` — use the shared
      // one styling-window persists with.
      sections.forEach((section) => {
        if (fieldsValue[section]) {
          if (!effectivePending[section]) (effectivePending as any)[section] = {};
          const sectionObj = effectivePending[section] as any;
          if (!sectionObj[stateProp]) sectionObj[stateProp] = {};
          const sectionData = fieldsValue[section] as any;
          const allowed = STATE_ALLOWED_PROPS[section as string] || [];
          const stateProps: Record<string, any> = {};
          allowed.forEach((key) => {
            if (sectionData[key] !== undefined) stateProps[key] = sectionData[key];
          });
          Object.assign(sectionObj[stateProp], stateProps);
        }
      });
    }

    if (saveTarget === "unified") {
      // Unified style - update by groupId
      // Update Position style
      const positionIndex = project.stylingPosition?.findIndex((s) => s.groupId === groupId);
      if (positionIndex !== undefined && positionIndex >= 0) {
        const existingEntry = project.stylingPosition[positionIndex];
        project.stylingPosition[positionIndex] = {
          ...existingEntry,
          name,
          ...(isInState
            ? {}
            : {
                isMarginLocked: fieldsValue.position?.isMarginLocked,
                isPaddingLocked: fieldsValue.position?.isPaddingLocked,
                marginTop: fieldsValue.position?.marginTop,
                marginRight: fieldsValue.position?.marginRight,
                marginBottom: fieldsValue.position?.marginBottom,
                marginLeft: fieldsValue.position?.marginLeft,
                paddingTop: fieldsValue.position?.paddingTop,
                paddingRight: fieldsValue.position?.paddingRight,
                paddingBottom: fieldsValue.position?.paddingBottom,
                paddingLeft: fieldsValue.position?.paddingLeft,
                width: fieldsValue.position?.width,
                height: fieldsValue.position?.height,
                widthStyle: fieldsValue.position?.widthStyle,
                heightStyle: fieldsValue.position?.heightStyle,
                maxWidth: fieldsValue.position?.maxWidth,
                maxHeight: fieldsValue.position?.maxHeight,
                maxWidthStyle: fieldsValue.position?.maxWidthStyle,
                maxHeightStyle: fieldsValue.position?.maxHeightStyle,
                minWidth: fieldsValue.position?.minWidth,
                minHeight: fieldsValue.position?.minHeight,
                minWidthStyle: fieldsValue.position?.minWidthStyle,
                minHeightStyle: fieldsValue.position?.minHeightStyle,
                alignItems: fieldsValue.position?.alignItems,
                textAlign: fieldsValue.position?.textAlign,
                display: fieldsValue.position?.display,
                xAxis: fieldsValue.position?.xAxis,
                yAxis: fieldsValue.position?.yAxis
              }),
          hoverStyles: (effectivePending?.position as any)?.hoverStyles ?? existingEntry.hoverStyles,
          pressedStyles: (effectivePending?.position as any)?.pressedStyles ?? existingEntry.pressedStyles,
          focusedStyles: (effectivePending?.position as any)?.focusedStyles ?? existingEntry.focusedStyles,
          disabledStyles: (effectivePending?.position as any)?.disabledStyles ?? existingEntry.disabledStyles
        };
      }

      // Update Typography style
      const typoIndex = project.stylingTypography?.findIndex((s) => s.groupId === groupId);
      if (typoIndex !== undefined && typoIndex >= 0) {
        const existingEntry = project.stylingTypography[typoIndex];
        project.stylingTypography[typoIndex] = {
          ...existingEntry,
          name,
          ...(isInState
            ? {}
            : {
                fontFamily: fieldsValue.text?.fontFamily,
                fontWeight: fieldsValue.text?.fontWeight,
                fontStyle: fieldsValue.text?.fontStyle,
                fontSize: fieldsValue.text?.fontSize,
                fontSizeStyle: fieldsValue.text?.fontSizeStyle,
                color: fieldsValue.text?.color,
                lineHeight: fieldsValue.text?.lineHeight,
                lineHeightStyle: fieldsValue.text?.lineHeightStyle,
                letterSpacing: fieldsValue.text?.letterSpacing,
                letterSpacingStyle: fieldsValue.text?.letterSpacingStyle,
                textDecoration: fieldsValue.text?.textDecoration,
                textTransform: fieldsValue.text?.textTransform,
                dontAffectedMobileSystem: fieldsValue.text?.dontAffectedMobileSystem
              }),
          hoverStyles: (effectivePending?.text as any)?.hoverStyles ?? existingEntry.hoverStyles,
          pressedStyles: (effectivePending?.text as any)?.pressedStyles ?? existingEntry.pressedStyles,
          focusedStyles: (effectivePending?.text as any)?.focusedStyles ?? existingEntry.focusedStyles,
          disabledStyles: (effectivePending?.text as any)?.disabledStyles ?? existingEntry.disabledStyles
        };
      }

      // Update Fill style
      const fillIndex = project.stylingFill?.findIndex((s) => s.groupId === groupId);
      if (fillIndex !== undefined && fillIndex >= 0) {
        const existingEntry = project.stylingFill[fillIndex];
        project.stylingFill[fillIndex] = {
          ...existingEntry,
          name,
          ...(isInState
            ? {}
            : {
                isVisible: fieldsValue.fill?.isVisible,
                color: fieldsValue.fill?.color,
                backgroundImage: fieldsValue.fill?.backgroundImage,
                repeatSwitch: fieldsValue.fill?.repeatSwitch,
                bgImageSize: fieldsValue.fill?.bgImageSize,
                stretch: fieldsValue.fill?.stretch,
                imageFit: fieldsValue.fill?.imageFit,
                bgImagePosition: fieldsValue.fill?.bgImagePosition
              }),
          hoverStyles: (effectivePending?.fill as any)?.hoverStyles ?? existingEntry.hoverStyles,
          pressedStyles: (effectivePending?.fill as any)?.pressedStyles ?? existingEntry.pressedStyles,
          focusedStyles: (effectivePending?.fill as any)?.focusedStyles ?? existingEntry.focusedStyles,
          disabledStyles: (effectivePending?.fill as any)?.disabledStyles ?? existingEntry.disabledStyles
        };
      }

      // Update Border style
      const borderIndex = project.stylingBorder?.findIndex((s) => s.groupId === groupId);
      if (borderIndex !== undefined && borderIndex >= 0) {
        const existingEntry = project.stylingBorder[borderIndex];
        project.stylingBorder[borderIndex] = {
          ...existingEntry,
          name,
          ...(isInState
            ? {}
            : {
                isVisible: fieldsValue.border?.isVisible,
                borderColor: fieldsValue.border?.borderColor,
                isBorderWidthLocked: fieldsValue.border?.isBorderWidthLocked,
                borderTopWidth: fieldsValue.border?.borderTopWidth,
                borderRightWidth: fieldsValue.border?.borderRightWidth,
                borderBottomWidth: fieldsValue.border?.borderBottomWidth,
                borderLeftWidth: fieldsValue.border?.borderLeftWidth,
                isBorderRadiusLocked: fieldsValue.border?.isBorderRadiusLocked,
                borderTopLeftRadius: fieldsValue.border?.borderTopLeftRadius,
                borderTopRightRadius: fieldsValue.border?.borderTopRightRadius,
                borderBottomRightRadius: fieldsValue.border?.borderBottomRightRadius,
                borderBottomLeftRadius: fieldsValue.border?.borderBottomLeftRadius,
                borderStyle: fieldsValue.border?.borderStyle
              }),
          hoverStyles: (effectivePending?.border as any)?.hoverStyles ?? existingEntry.hoverStyles,
          pressedStyles: (effectivePending?.border as any)?.pressedStyles ?? existingEntry.pressedStyles,
          focusedStyles: (effectivePending?.border as any)?.focusedStyles ?? existingEntry.focusedStyles,
          disabledStyles: (effectivePending?.border as any)?.disabledStyles ?? existingEntry.disabledStyles
        };
      }

      // Update Shadow style
      const shadowIndex = project.stylingShadow?.findIndex((s) => s.groupId === groupId);
      if (shadowIndex !== undefined && shadowIndex >= 0) {
        const existingEntry = project.stylingShadow[shadowIndex];
        project.stylingShadow[shadowIndex] = {
          ...existingEntry,
          name,
          ...(isInState
            ? {}
            : {
                isVisible: fieldsValue.shadow?.isVisible,
                color: fieldsValue.shadow?.color,
                x: fieldsValue.shadow?.x,
                y: fieldsValue.shadow?.y,
                b: fieldsValue.shadow?.b,
                s: fieldsValue.shadow?.s
              }),
          hoverStyles: (effectivePending?.shadow as any)?.hoverStyles ?? existingEntry.hoverStyles,
          pressedStyles: (effectivePending?.shadow as any)?.pressedStyles ?? existingEntry.pressedStyles,
          focusedStyles: (effectivePending?.shadow as any)?.focusedStyles ?? existingEntry.focusedStyles,
          disabledStyles: (effectivePending?.shadow as any)?.disabledStyles ?? existingEntry.disabledStyles
        };
      }

      // Update Effects style
      const effectsIndex = project.stylingEffects?.findIndex((s) => s.groupId === groupId);
      if (effectsIndex !== undefined && effectsIndex >= 0) {
        const existingEntry = project.stylingEffects[effectsIndex];
        project.stylingEffects[effectsIndex] = {
          ...existingEntry,
          name,
          ...(isInState || !fieldsValue.effects ? {} : pickEffectsBaseProps(fieldsValue.effects)),
          hoverStyles: (effectivePending?.effects as any)?.hoverStyles ?? existingEntry.hoverStyles,
          pressedStyles: (effectivePending?.effects as any)?.pressedStyles ?? existingEntry.pressedStyles,
          focusedStyles: (effectivePending?.effects as any)?.focusedStyles ?? existingEntry.focusedStyles,
          disabledStyles: (effectivePending?.effects as any)?.disabledStyles ?? existingEntry.disabledStyles
        };
      }
    } else {
      // Legacy style - update by individual panel ID
      // Check and update Position style
      if (fieldsValue.position?.positionId) {
        const positionIndex = project.stylingPosition?.findIndex(
          (s) => s.positionId?.toLowerCase() === fieldsValue.position?.positionId?.toLowerCase()
        );
        if (positionIndex !== undefined && positionIndex >= 0) {
          // Extract base name without suffix (e.g., "MyStyle_Layout" -> "MyStyle")
          const baseName = name.replace(/_Layout$/, "");
          project.stylingPosition[positionIndex] = {
            ...project.stylingPosition[positionIndex],
            name: baseName,
            isMarginLocked: fieldsValue.position?.isMarginLocked,
            isPaddingLocked: fieldsValue.position?.isPaddingLocked,
            marginTop: fieldsValue.position?.marginTop,
            marginRight: fieldsValue.position?.marginRight,
            marginBottom: fieldsValue.position?.marginBottom,
            marginLeft: fieldsValue.position?.marginLeft,
            paddingTop: fieldsValue.position?.paddingTop,
            paddingRight: fieldsValue.position?.paddingRight,
            paddingBottom: fieldsValue.position?.paddingBottom,
            paddingLeft: fieldsValue.position?.paddingLeft,
            width: fieldsValue.position?.width,
            height: fieldsValue.position?.height,
            widthStyle: fieldsValue.position?.widthStyle,
            heightStyle: fieldsValue.position?.heightStyle,
            maxWidth: fieldsValue.position?.maxWidth,
            maxHeight: fieldsValue.position?.maxHeight,
            maxWidthStyle: fieldsValue.position?.maxWidthStyle,
            maxHeightStyle: fieldsValue.position?.maxHeightStyle,
            minWidth: fieldsValue.position?.minWidth,
            minHeight: fieldsValue.position?.minHeight,
            minWidthStyle: fieldsValue.position?.minWidthStyle,
            minHeightStyle: fieldsValue.position?.minHeightStyle,
            alignItems: fieldsValue.position?.alignItems,
            textAlign: fieldsValue.position?.textAlign,
            display: fieldsValue.position?.display,
            xAxis: fieldsValue.position?.xAxis,
            yAxis: fieldsValue.position?.yAxis
          };
        }
      }

      // Check and update Typography style
      if (fieldsValue.text?.typoId) {
        const typoIndex = project.stylingTypography?.findIndex(
          (s) => s.typoId?.toLowerCase() === fieldsValue.text?.typoId?.toLowerCase()
        );
        if (typoIndex !== undefined && typoIndex >= 0) {
          const baseName = name.replace(/_Text$/, "");
          project.stylingTypography[typoIndex] = {
            ...project.stylingTypography[typoIndex],
            name: baseName,
            fontFamily: fieldsValue.text?.fontFamily,
            fontWeight: fieldsValue.text?.fontWeight,
            fontStyle: fieldsValue.text?.fontStyle,
            fontSize: fieldsValue.text?.fontSize,
            fontSizeStyle: fieldsValue.text?.fontSizeStyle,
            color: fieldsValue.text?.color,
            lineHeight: fieldsValue.text?.lineHeight,
            lineHeightStyle: fieldsValue.text?.lineHeightStyle,
            letterSpacing: fieldsValue.text?.letterSpacing,
            letterSpacingStyle: fieldsValue.text?.letterSpacingStyle,
            textDecoration: fieldsValue.text?.textDecoration,
            textTransform: fieldsValue.text?.textTransform,
            dontAffectedMobileSystem: fieldsValue.text?.dontAffectedMobileSystem
          };
        }
      }

      // Check and update Fill style
      if (fieldsValue.fill?.fillId) {
        const fillIndex = project.stylingFill?.findIndex(
          (s) => s.fillId?.toLowerCase() === fieldsValue.fill?.fillId?.toLowerCase()
        );
        if (fillIndex !== undefined && fillIndex >= 0) {
          const baseName = name.replace(/_Fill$/, "");
          project.stylingFill[fillIndex] = {
            ...project.stylingFill[fillIndex],
            name: baseName,
            isVisible: fieldsValue.fill?.isVisible,
            color: fieldsValue.fill?.color,
            backgroundImage: fieldsValue.fill?.backgroundImage,
            repeatSwitch: fieldsValue.fill?.repeatSwitch,
            bgImageSize: fieldsValue.fill?.bgImageSize,
            stretch: fieldsValue.fill?.stretch,
            imageFit: fieldsValue.fill?.imageFit,
            bgImagePosition: fieldsValue.fill?.bgImagePosition
          };
        }
      }

      // Check and update Border style
      if (fieldsValue.border?.borderId) {
        const borderIndex = project.stylingBorder?.findIndex(
          (s) => s.borderId?.toLowerCase() === fieldsValue.border?.borderId?.toLowerCase()
        );
        if (borderIndex !== undefined && borderIndex >= 0) {
          const baseName = name.replace(/_Border$/, "");
          project.stylingBorder[borderIndex] = {
            ...project.stylingBorder[borderIndex],
            name: baseName,
            isVisible: fieldsValue.border?.isVisible,
            borderColor: fieldsValue.border?.borderColor,
            isBorderWidthLocked: fieldsValue.border?.isBorderWidthLocked,
            borderTopWidth: fieldsValue.border?.borderTopWidth,
            borderRightWidth: fieldsValue.border?.borderRightWidth,
            borderBottomWidth: fieldsValue.border?.borderBottomWidth,
            borderLeftWidth: fieldsValue.border?.borderLeftWidth,
            isBorderRadiusLocked: fieldsValue.border?.isBorderRadiusLocked,
            borderTopLeftRadius: fieldsValue.border?.borderTopLeftRadius,
            borderTopRightRadius: fieldsValue.border?.borderTopRightRadius,
            borderBottomRightRadius: fieldsValue.border?.borderBottomRightRadius,
            borderBottomLeftRadius: fieldsValue.border?.borderBottomLeftRadius,
            borderStyle: fieldsValue.border?.borderStyle
          };
        }
      }

      // Check and update Shadow style
      if (fieldsValue.shadow?.shadowId) {
        const shadowIndex = project.stylingShadow?.findIndex(
          (s) => s.shadowId?.toLowerCase() === fieldsValue.shadow?.shadowId?.toLowerCase()
        );
        if (shadowIndex !== undefined && shadowIndex >= 0) {
          const baseName = name.replace(/_Shadow$/, "");
          project.stylingShadow[shadowIndex] = {
            ...project.stylingShadow[shadowIndex],
            name: baseName,
            isVisible: fieldsValue.shadow?.isVisible,
            color: fieldsValue.shadow?.color,
            x: fieldsValue.shadow?.x,
            y: fieldsValue.shadow?.y,
            b: fieldsValue.shadow?.b,
            s: fieldsValue.shadow?.s
          };
        }
      }

      // Check and update Effects style
      if (fieldsValue.effects?.effectsId) {
        const effectsIndex = project.stylingEffects?.findIndex(
          (s) => s.effectsId?.toLowerCase() === fieldsValue.effects?.effectsId?.toLowerCase()
        );
        if (effectsIndex !== undefined && effectsIndex >= 0) {
          const baseName = name.replace(/_Effects$/, "");
          project.stylingEffects[effectsIndex] = {
            ...project.stylingEffects[effectsIndex],
            name: baseName,
            ...(fieldsValue.effects ? pickEffectsBaseProps(fieldsValue.effects) : {})
          };
        }
      }

      // KOA-15838: persist the state-mode overlays (hover/pressed/focused/
      // disabled) for the single-panel (legacy) tag. The per-section base writes
      // above match by `fieldsValue.X.Xid` — which a state switch nulls — and
      // never write `*Styles`, so a legacy tag's hover/pressed/focused colours
      // were silently dropped on save. Resolve the edited collection from
      // `editingGroupId` and the stable `editingTagValue` (the section id), then
      // merge the overlays accumulated in `effectivePending`. `?? existing`
      // preserves any overlay the user did not touch this session.
      const legacyTagId: string | null = this.state.editingTagValue;
      if (legacyTagId) {
        const idLower: string = legacyTagId.toLowerCase();
        if (editingGroupId === "layout") {
          const idx = project.stylingPosition?.findIndex((s) => s.positionId?.toLowerCase() === idLower);
          if (idx !== undefined && idx >= 0) {
            const entry = project.stylingPosition[idx];
            const pend = effectivePending?.position as any;
            project.stylingPosition[idx] = {
              ...entry,
              hoverStyles: pend?.hoverStyles ?? entry.hoverStyles,
              pressedStyles: pend?.pressedStyles ?? entry.pressedStyles,
              focusedStyles: pend?.focusedStyles ?? entry.focusedStyles,
              disabledStyles: pend?.disabledStyles ?? entry.disabledStyles
            };
          }
        } else if (editingGroupId === "text") {
          const idx = project.stylingTypography?.findIndex((s) => s.typoId?.toLowerCase() === idLower);
          if (idx !== undefined && idx >= 0) {
            const entry = project.stylingTypography[idx];
            const pend = effectivePending?.text as any;
            project.stylingTypography[idx] = {
              ...entry,
              hoverStyles: pend?.hoverStyles ?? entry.hoverStyles,
              pressedStyles: pend?.pressedStyles ?? entry.pressedStyles,
              focusedStyles: pend?.focusedStyles ?? entry.focusedStyles,
              disabledStyles: pend?.disabledStyles ?? entry.disabledStyles
            };
          }
        } else if (editingGroupId === "fill") {
          const idx = project.stylingFill?.findIndex((s) => s.fillId?.toLowerCase() === idLower);
          if (idx !== undefined && idx >= 0) {
            const entry = project.stylingFill[idx];
            const pend = effectivePending?.fill as any;
            project.stylingFill[idx] = {
              ...entry,
              hoverStyles: pend?.hoverStyles ?? entry.hoverStyles,
              pressedStyles: pend?.pressedStyles ?? entry.pressedStyles,
              focusedStyles: pend?.focusedStyles ?? entry.focusedStyles,
              disabledStyles: pend?.disabledStyles ?? entry.disabledStyles
            };
          }
        } else if (editingGroupId === "border") {
          const idx = project.stylingBorder?.findIndex((s) => s.borderId?.toLowerCase() === idLower);
          if (idx !== undefined && idx >= 0) {
            const entry = project.stylingBorder[idx];
            const pend = effectivePending?.border as any;
            project.stylingBorder[idx] = {
              ...entry,
              hoverStyles: pend?.hoverStyles ?? entry.hoverStyles,
              pressedStyles: pend?.pressedStyles ?? entry.pressedStyles,
              focusedStyles: pend?.focusedStyles ?? entry.focusedStyles,
              disabledStyles: pend?.disabledStyles ?? entry.disabledStyles
            };
          }
        } else if (editingGroupId === "shadow") {
          const idx = project.stylingShadow?.findIndex((s) => s.shadowId?.toLowerCase() === idLower);
          if (idx !== undefined && idx >= 0) {
            const entry = project.stylingShadow[idx];
            const pend = effectivePending?.shadow as any;
            project.stylingShadow[idx] = {
              ...entry,
              hoverStyles: pend?.hoverStyles ?? entry.hoverStyles,
              pressedStyles: pend?.pressedStyles ?? entry.pressedStyles,
              focusedStyles: pend?.focusedStyles ?? entry.focusedStyles,
              disabledStyles: pend?.disabledStyles ?? entry.disabledStyles
            };
          }
        }
      }
    }

    // KOA-15838 base-drop fix: when the save fires while a NON-none state is
    // active, the unified branch wrote {} for base props (the isInState guard)
    // and the legacy base writes were gated by the form section id (which a
    // state switch nulls) — so None/base edits made before entering the state
    // were dropped. Those edits live only in statePreviewBackup (the clean base
    // snapshot taken at state-entry; pendingTagStateStyles holds states, not
    // base). Overlay that clean base onto the saved entries so base edits
    // persist regardless of which state was active at save time.
    if (isInState && this.props.statePreviewBackup) {
      const backup: IKuikaStyle = this.props.statePreviewBackup;
      const overlayBase = (
        entry: StyleSectionEntry | undefined,
        backupSection: StyleSectionEntry | undefined
      ): void => {
        if (!entry || !backupSection) return;
        // Strip ids / group / name / metadata / state sub-objects; everything
        // else is a base CSS or control prop that should carry over.
        const {
          hoverStyles: _h,
          pressedStyles: _p,
          focusedStyles: _f,
          disabledStyles: _d,
          positionId: _pid,
          positionIds: _pids,
          typoId: _tid,
          typoIds: _tids,
          fillId: _fid,
          fillIds: _fids,
          borderId: _bid,
          borderIds: _bids,
          shadowId: _sid,
          shadowIds: _sids,
          effectsId: _eid,
          effectsIds: _eids,
          groupId: _g,
          name: _n,
          styleType: _st,
          componentType: _ct,
          kuikaid: _k,
          id: _id,
          overridden: _o,
          ...baseProps
        } = backupSection as any;
        Object.assign(entry, baseProps);
      };

      if (saveTarget === "unified") {
        overlayBase(
          project.stylingPosition?.find((s) => s.groupId === groupId),
          backup.position
        );
        overlayBase(
          project.stylingTypography?.find((s) => s.groupId === groupId),
          backup.text
        );
        overlayBase(
          project.stylingFill?.find((s) => s.groupId === groupId),
          backup.fill
        );
        overlayBase(
          project.stylingBorder?.find((s) => s.groupId === groupId),
          backup.border
        );
        overlayBase(
          project.stylingShadow?.find((s) => s.groupId === groupId),
          backup.shadow
        );
        overlayBase(
          project.stylingEffects?.find((s) => s.groupId === groupId),
          backup.effects
        );
      } else {
        const idLower: string | undefined = this.state.editingTagValue?.toLowerCase();
        if (editingGroupId === "layout") {
          overlayBase(
            project.stylingPosition?.find((s) => s.positionId?.toLowerCase() === idLower),
            backup.position
          );
        } else if (editingGroupId === "text") {
          overlayBase(
            project.stylingTypography?.find((s) => s.typoId?.toLowerCase() === idLower),
            backup.text
          );
        } else if (editingGroupId === "fill") {
          overlayBase(
            project.stylingFill?.find((s) => s.fillId?.toLowerCase() === idLower),
            backup.fill
          );
        } else if (editingGroupId === "border") {
          overlayBase(
            project.stylingBorder?.find((s) => s.borderId?.toLowerCase() === idLower),
            backup.border
          );
        } else if (editingGroupId === "shadow") {
          overlayBase(
            project.stylingShadow?.find((s) => s.shadowId?.toLowerCase() === idLower),
            backup.shadow
          );
        } else if (editingGroupId === "effects") {
          overlayBase(
            project.stylingEffects?.find((s) => s.effectsId?.toLowerCase() === idLower),
            backup.effects
          );
        }
      }
    }

    await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);
    this.cancelEdit();
  };

  // Cancel edit mode - restore form values from backup
  cancelEdit = () => {
    const { formBackup } = this.state;
    this.setState({ mode: EditMode.custom, editingTagValue: null, formBackup: null, isEditingNewTag: false });
    this.props.onEditModeChange?.(false, null);

    // Reset state to "none" without triggering full handleStateChange
    if (this.props.selectedState !== "none") {
      this.props.onStateReset?.();
    }

    if (formBackup) {
      // Restore form values from backup - use resetFields first like panels do
      const restoredFieldsValue = _.cloneDeep(formBackup);
      if (restoredFieldsValue.position) restoredFieldsValue.position.positionId = null;
      if (restoredFieldsValue.text) restoredFieldsValue.text.typoId = null;
      if (restoredFieldsValue.fill) restoredFieldsValue.fill.fillId = null;
      if (restoredFieldsValue.border) restoredFieldsValue.border.borderId = null;
      if (restoredFieldsValue.shadow) restoredFieldsValue.shadow.shadowId = null;
      if (restoredFieldsValue.effects) restoredFieldsValue.effects.effectsId = null;
      restoredFieldsValue.styleGroupId = null;
      restoredFieldsValue.styleGroupName = undefined;

      this.props.form.resetFields();
      this.props.form.setFieldsValue(restoredFieldsValue);
      this.props.form.submit();
    } else {
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue.position) fieldsValue.position.positionId = null;
      if (fieldsValue.text) fieldsValue.text.typoId = null;
      if (fieldsValue.fill) fieldsValue.fill.fillId = null;
      if (fieldsValue.border) fieldsValue.border.borderId = null;
      if (fieldsValue.shadow) fieldsValue.shadow.shadowId = null;
      if (fieldsValue.effects) fieldsValue.effects.effectsId = null;
      fieldsValue.styleGroupId = null;
      fieldsValue.styleGroupName = undefined;

      this.props.form.resetFields();
      this.props.form.setFieldsValue(fieldsValue);
      this.props.form.submit();
    }
  };

  // Remove style from current component
  removeFromComponent = (styleItem: IUnifiedStyleItem, performTagOnClose?: () => void) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    removeStyleTagIds(fieldsValue, styleItem);

    this.props.form.setFieldsValue(fieldsValue);
    this.props.form.submit();

    if (performTagOnClose) {
      performTagOnClose();
    }
  };

  // Delete style from project
  deleteFromProject = async () => {
    const styleItem = this.state.deleteArguments.deletingStyleItem;
    if (!styleItem) return;

    const project = _.cloneDeep(this.props.sd.project);

    if (styleItem.isLegacy) {
      // Delete only the specific panel's style
      if (styleItem.panelType === "Layout") {
        project.stylingPosition = project.stylingPosition?.filter(
          (s) => s.positionId?.toLowerCase() !== styleItem.id.toLowerCase()
        );
      } else if (styleItem.panelType === "Text") {
        project.stylingTypography = project.stylingTypography?.filter(
          (s) => s.typoId?.toLowerCase() !== styleItem.id.toLowerCase()
        );
      } else if (styleItem.panelType === "Fill") {
        project.stylingFill = project.stylingFill?.filter(
          (s) => s.fillId?.toLowerCase() !== styleItem.id.toLowerCase()
        );
      } else if (styleItem.panelType === "Border") {
        project.stylingBorder = project.stylingBorder?.filter(
          (s) => s.borderId?.toLowerCase() !== styleItem.id.toLowerCase()
        );
      } else if (styleItem.panelType === "Shadow") {
        project.stylingShadow = project.stylingShadow?.filter(
          (s) => s.shadowId?.toLowerCase() !== styleItem.id.toLowerCase()
        );
      } else if (styleItem.panelType === "Effects") {
        project.stylingEffects = project.stylingEffects?.filter(
          (s) => s.effectsId?.toLowerCase() !== styleItem.id.toLowerCase()
        );
      }
    } else {
      // Delete all linked styles by groupId
      const groupId = styleItem.groupId;
      project.stylingPosition = project.stylingPosition?.filter((s) => s.groupId !== groupId);
      project.stylingTypography = project.stylingTypography?.filter((s) => s.groupId !== groupId);
      project.stylingFill = project.stylingFill?.filter((s) => s.groupId !== groupId);
      project.stylingBorder = project.stylingBorder?.filter((s) => s.groupId !== groupId);
      project.stylingShadow = project.stylingShadow?.filter((s) => s.groupId !== groupId);
      project.stylingEffects = project.stylingEffects?.filter((s) => s.groupId !== groupId);
    }

    await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);

    // Also remove from component
    this.removeFromComponent(styleItem, this.state.deleteArguments.performStyleTagOnClose);

    this.setState({
      deleteArguments: {
        isDeleteStyleModalVisible: false,
        deletingStyleItem: null,
        performStyleTagOnClose: undefined
      }
    });
  };

  // Validate style name
  validateStyleName = (styleName: string | undefined, isEditing?: boolean): string | null => {
    if (!styleName || styleName.trim().length === 0) {
      KMainFunctions.displayWarningNotification("Please enter a valid style name.");
      return null;
    }

    const unifiedList = this.getUnifiedStyleList();
    const editingId = this.state.editingTagValue;

    // When editing, exclude the current style from the name check
    if (isEditing && editingId) {
      const currentStyle = unifiedList.find((s) => s.id === editingId);
      const currentStyleName = currentStyle?.displayName;

      const isNameExist = unifiedList
        .filter((s) => s.displayName.toLowerCase() !== currentStyleName?.toLowerCase())
        .some((s) => s.displayName.toLowerCase() === styleName.toLowerCase());

      if (isNameExist) {
        KMainFunctions.displayWarningNotification(
          `The style name "${styleName}" already exists. Please choose a different name.`
        );
        return null;
      }
    } else {
      const isNameExist = unifiedList.some((s) => s.displayName.toLowerCase() === styleName.toLowerCase());

      if (isNameExist) {
        KMainFunctions.displayWarningNotification(
          `The style name "${styleName}" already exists. Please choose a different name.`
        );
        return null;
      }
    }

    return styleName;
  };

  handleSearch = (value: string) => {
    this.setState({ searchText: value });
  };

  handleCreateNewStyle = async (styleName: string) => {
    if (!styleName || this.isCreatingStyle) return;

    this.setState({ searchText: "", isDropdownVisible: false });

    const validName = this.validateStyleName(styleName);
    if (!validName) return;

    this.isCreatingStyle = true;
    this.setState({ creatingStyleName: validName });
    // The tag is attached to the component selected now. If another is selected before the round trip
    // ends, edit mode would open on that one, where the new chip is not, and leave no ✓ or ✗ to end it.
    const selectionAtCreate: string[] = this.props.sd.selectedKuikaIds;

    let groupId: string;
    try {
      groupId = await this.createUnifiedStyle(validName);
    } catch (error) {
      this.finishCreatingStyle();
      throw error;
    }

    // Enter edit mode for the new style. That comes first so the loader hands straight over to the
    // "New style" note, with no frame where the field shows neither.
    setTimeout(() => {
      const isSameSelection: boolean = _.isEqual(this.props.sd.selectedKuikaIds, selectionAtCreate);
      const unifiedList = this.getUnifiedStyleList();
      const newStyle = unifiedList.find((s) => s.groupId === groupId);
      if (newStyle && isSameSelection) {
        this.editUnifiedStyle(newStyle, true);
      }
      this.finishCreatingStyle();
    }, 500);
  };

  finishCreatingStyle = (): void => {
    this.isCreatingStyle = false;
    this.setState({ creatingStyleName: null });
  };

  // The "+" beside the chips is a second way into the search the field itself opens on click: the
  // list opens and the caret lands in the search box, where typing filters or offers "Add <name>".
  // Refused while a tag is being edited or created, as the field's own open is
  // (handleDropdownVisibleChange).
  openStyleSearch = (): void => {
    if (this.state.mode === EditMode.edit || this.isCreatingStyle) return;
    this.setState({ isDropdownVisible: true, activeStylingMenuTag: null });
    document.getElementById(STYLE_SEARCH_INPUT_ID)?.focus();
  };

  // The "Style" caption is the search box's <label>. The browser's own label activation focuses the
  // box whatever `openStyleSearch` decides, so a refused click also cancels it. Otherwise focus
  // lands in the zero-width box mid-edit and typing changes the search.
  handleStyleCaptionClick = (e: React.MouseEvent<HTMLLabelElement>): void => {
    if (this.state.mode === EditMode.edit || this.isCreatingStyle) {
      e.preventDefault();
      return;
    }
    this.openStyleSearch();
  };

  handleBackspace = (e: React.KeyboardEvent<any>) => {
    if (this.state.searchText && this.state.searchText.length > 0) return;

    const selectedIds = this.getSelectedStyleIds();
    if (selectedIds.length === 0) return;

    const lastId = selectedIds[selectedIds.length - 1];
    const unifiedList = this.getUnifiedStyleList();
    const styleItem = unifiedList.find((s) => s.id.toLowerCase() === lastId.toLowerCase());

    if (styleItem) {
      this.removeFromComponent(styleItem);
    }
  };

  handleDropdownVisibleChange = (visible: boolean) => {
    if (visible && this.state.mode === EditMode.edit && this.state.editingTagValue) {
      return;
    }
    if (visible && this.isCreatingStyle) {
      return;
    }
    this.setState({ isDropdownVisible: visible, activeStylingMenuTag: null });
  };

  handleChange = (value: string | number | Array<string | number> | null) => {
    const unifiedList = this.getUnifiedStyleList();
    const currentSelectedIds = this.getSelectedStyleIds();

    // Handle empty value - all styles deselected
    if (!value || !Array.isArray(value)) {
      // Remove all styles
      currentSelectedIds.forEach((id) => {
        const styleItem = unifiedList.find((s) => s.id.toLowerCase() === id.toLowerCase());
        if (styleItem) {
          this.removeFromComponent(styleItem);
        }
      });
      return;
    }

    // Check if it's a deselection (value array is shorter)
    const valueStrings = value.map((v) => String(v).toLowerCase());
    const removedIds = currentSelectedIds.filter((id) => !valueStrings.includes(id.toLowerCase()));

    if (removedIds.length > 0) {
      // Handle deselection
      removedIds.forEach((removedId) => {
        const styleItem = unifiedList.find((s) => s.id.toLowerCase() === removedId.toLowerCase());
        if (styleItem) {
          this.removeFromComponent(styleItem);
        }
      });
      this.setState({ searchText: "" });
      return;
    }

    // Handle selection (value array is longer or same with new item)
    if (value.length === 0) return;

    const lastSelectedValue = value[value.length - 1];

    // Check if selecting an existing style
    const existingStyle = unifiedList.find(
      (item) =>
        item.id.toLowerCase() === String(lastSelectedValue).toLowerCase() ||
        item.displayName.toLowerCase() === String(lastSelectedValue).toLowerCase()
    );

    if (existingStyle) {
      // Apply the style to the component
      this.applyStyleToComponent(existingStyle);
    }
    // A value that is not a listed style is the search text itself: rc-select's tags mode commits
    // whatever was typed when the field loses focus. Creating from here made a project-wide style
    // out of an abandoned search — the user typed a few letters to filter, clicked elsewhere, and
    // owned a new tag in all five styling collections. Creating a style is the "Add" row and Enter,
    // both of which call handleCreateNewStyle themselves; anything else drops the text.

    this.setState({ searchText: "" });
  };

  applyStyleToComponent = (styleItem: IUnifiedStyleItem) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    applyStyleTagIds(fieldsValue, styleItem);

    this.props.form.setFieldsValue(fieldsValue);
    this.props.form.submit();
  };

  getFriendlyName = (): string => {
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    return selectedDi?.friendlyName || "";
  };

  isDefaultStyling = (styleId: string): boolean => {
    // Check if any of the style types is a default styling
    const defaultStyles = this.props.sd.defaultStyleProvider?.getDesktopStyles();
    if (!defaultStyles) return false;

    return (
      defaultStyles.position?.some((s) => s.positionId?.toLowerCase() === styleId.toLowerCase()) ||
      defaultStyles.typography?.some((s) => s.typoId?.toLowerCase() === styleId.toLowerCase()) ||
      defaultStyles.fill?.some((s) => s.fillId?.toLowerCase() === styleId.toLowerCase()) ||
      defaultStyles.border?.some((s) => s.borderId?.toLowerCase() === styleId.toLowerCase()) ||
      defaultStyles.shadow?.some((s) => s.shadowId?.toLowerCase() === styleId.toLowerCase()) ||
      defaultStyles.effects?.some((s) => s.effectsId?.toLowerCase() === styleId.toLowerCase()) ||
      false
    );
  };

  isCurrentComponentDefaultStyling = (styleId: string): boolean => {
    return false; // For unified styles, this check is simplified
  };

  /**
   * Resolves the project-level styling entries for the tag currently being
   * edited. A unified tag shares one `groupId` across all five collections; a
   * single-panel (legacy) tag has NO `groupId` and is keyed by the edited panel
   * (`editingGroupId`) plus its section id (`editingTagValue`).
   *
   * KOA-15838: matching legacy tags only by `groupId` (the old code) returned
   * nothing, so the tag's saved state/base styles never lit the state-selector
   * indicators until a state switch populated `pendingTagStateStyles`.
   */
  getEditingTagSectionEntries = (): EditingTagSectionEntry[] => {
    const { editingTagValue } = this.state;
    if (!editingTagValue) return [];

    const { project } = this.props.sd;
    const editingGroupId = this.props.editingGroupId;
    const idLower = editingTagValue.toLowerCase();
    const result: EditingTagSectionEntry[] = [];
    const push = (section: keyof IKuikaStyle, entry: StyleSectionEntry | undefined): void => {
      if (entry) result.push({ section, entry });
    };

    if (editingGroupId === "unified") {
      push(
        "position",
        project.stylingPosition?.find((s) => s.groupId === editingTagValue)
      );
      push(
        "text",
        project.stylingTypography?.find((s) => s.groupId === editingTagValue)
      );
      push(
        "fill",
        project.stylingFill?.find((s) => s.groupId === editingTagValue)
      );
      push(
        "border",
        project.stylingBorder?.find((s) => s.groupId === editingTagValue)
      );
      push(
        "shadow",
        project.stylingShadow?.find((s) => s.groupId === editingTagValue)
      );
      push(
        "effects",
        project.stylingEffects?.find((s) => s.groupId === editingTagValue)
      );
    } else if (editingGroupId === "layout") {
      push(
        "position",
        project.stylingPosition?.find((s) => s.positionId?.toLowerCase() === idLower)
      );
    } else if (editingGroupId === "text") {
      push(
        "text",
        project.stylingTypography?.find((s) => s.typoId?.toLowerCase() === idLower)
      );
    } else if (editingGroupId === "fill") {
      push(
        "fill",
        project.stylingFill?.find((s) => s.fillId?.toLowerCase() === idLower)
      );
    } else if (editingGroupId === "border") {
      push(
        "border",
        project.stylingBorder?.find((s) => s.borderId?.toLowerCase() === idLower)
      );
    } else if (editingGroupId === "shadow") {
      push(
        "shadow",
        project.stylingShadow?.find((s) => s.shadowId?.toLowerCase() === idLower)
      );
    } else if (editingGroupId === "effects") {
      push(
        "effects",
        project.stylingEffects?.find((s) => s.effectsId?.toLowerCase() === idLower)
      );
    }

    return result;
  };

  /**
   * Checks whether the currently editing style tag (or the selected DI's styleObj) has saved styles for a given state.
   */
  hasStateStyles = (state: StyleState): boolean => {
    if (state === "none") return false;

    const stateProp = `${state}Styles`;
    const { editingTagValue } = this.state;

    // Check pending tag state styles first
    if (this.props.pendingTagStateStyles) {
      const pending = this.props.pendingTagStateStyles;
      const sections: (keyof IKuikaStyle)[] = ["position", "text", "fill", "border", "shadow", "effects"];
      for (const section of sections) {
        const sectionData = pending[section] as any;
        if (sectionData?.[stateProp] && Object.keys(sectionData[stateProp]).length > 0) {
          return true;
        }
      }
    }

    // If editing a style tag, check the project metadata for it (unified by
    // groupId, single-panel/legacy by section id — see getEditingTagSectionEntries).
    if (editingTagValue) {
      const entries = this.getEditingTagSectionEntries();
      for (const { entry } of entries) {
        const stateData = (entry as any)[stateProp];
        if (stateData && Object.keys(stateData).length > 0) {
          return true;
        }
      }
      return false;
    }

    // Otherwise check the selected DI's styleObj. KOA-15838: a referencing DI
    // (carries fillIds/typoIds/…) may hold an inline {state}Styles that is just
    // a leaked COPY of the tag's state value. That is INHERITED, not a local
    // change, so it must not light the orange "changed" dot — only a value that
    // DIFFERS from the resolved tag value counts (hasGenuineStateOverride).
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    if (selectedDi?.styleObj) {
      const styleObj = selectedDi.styleObj;
      const collections = this.props.sd.project as unknown as IStyleTagCollections;
      const sections: (keyof IKuikaStyle)[] = ["position", "text", "fill", "border", "shadow", "effects"];
      for (const section of sections) {
        const sectionData = (styleObj as any)[section] as Record<string, unknown> | undefined;
        const overlay = sectionData?.[stateProp] as Record<string, unknown> | undefined;
        if (!overlay || Object.keys(overlay).length === 0) continue;
        const allowed = STATE_ALLOWED_PROPS[section as string] || [];
        if (hasGenuineStateOverride(collections, section, sectionData, overlay, stateProp, allowed)) {
          return true;
        }
      }
    }

    return false;
  };

  /**
   * Lightweight scan of current form values to detect any non-default style content.
   * Mirrors styling-window's hasAnyValueInSection logic but is stateless about the source —
   * it reflects whatever the form is currently showing (base when selectedState==="none",
   * a specific state's values otherwise). Used inside Form.Item shouldUpdate so the indicator
   * reacts to keystrokes without waiting for the debounced submit.
   */
  hasAnyFormSectionValue = (formValues: IKuikaStyle | undefined): boolean => {
    if (!formValues) return false;

    const sections: (keyof IKuikaStyle)[] = ["position", "text", "fill", "border", "shadow", "effects"];
    const idCheckers: Record<string, string> = {
      position: "positionId",
      text: "typoId",
      fill: "fillId",
      border: "borderId",
      shadow: "shadowId",
      effects: "effectsId"
    };
    const excludeKeys: Record<string, string[]> = {
      position: [
        "positionIds",
        "display",
        "isPaddingLocked",
        "widthStyle",
        "heightStyle",
        "maxWidthStyle",
        "maxHeightStyle",
        "minWidthStyle",
        "minHeightStyle",
        "name",
        "componentType"
      ],
      text: ["typoIds", "fontSizeStyle", "name", "componentType"],
      shadow: ["shadowIds", "isVisible", "name", "componentType"],
      border: ["borderIds", "borderStyle", "isBorderRadiusLocked", "isBorderWidthLocked", "name", "componentType"],
      fill: [
        "fillIds",
        "bgImagePosition",
        "bgImageSize",
        "repeatSwitch",
        "stretch",
        "name",
        "imageFit",
        "componentType"
      ],
      effects: ["effectsIds", "kuikaid", "name", "componentType", "isVisible"]
    };

    const isNonEmpty = (value: any): boolean => {
      if (value === undefined || value === null || value === "" || value === "transparent") return false;
      if (Array.isArray(value)) return value.length > 0 && value.some(isNonEmpty);
      if (typeof value === "object") return Object.keys(value).length > 0 && Object.values(value).some(isNonEmpty);
      return true;
    };

    const selectedState = this.props.selectedState;
    const collections = this.props.sd.project as unknown as IStyleTagCollections;

    return sections.some((section) => {
      const sectionData = (formValues as any)[section] as Record<string, unknown> | undefined;
      if (!sectionData) return false;
      if (sectionData[idCheckers[section]]) return false;
      const excluded = excludeKeys[section as string] || [];
      const hasValue = Object.keys(sectionData).some((key) => !excluded.includes(key) && isNonEmpty(sectionData[key]));
      if (!hasValue) return false;
      // KOA-15838: in a non-none state, the form holds that state's overlay. A
      // value equal to the referenced tag's resolved state value is INHERITED
      // (class), not a local change — so it must not light the dot. Only a
      // genuine override (different from the tag) counts.
      if (selectedState !== "none") {
        const allowed = STATE_ALLOWED_PROPS[section as string] || [];
        return hasGenuineStateOverride(
          collections,
          section,
          sectionData,
          sectionData,
          `${selectedState}Styles`,
          allowed
        );
      }
      return true;
    });
  };

  /**
   * Checks whether the currently editing style tag (or the selected DI's styleObj) has any
   * non-empty base style props. Used to decide whether to show the "changed indicator" on the
   * "None" state option, mirroring how hasStateStyles drives indicators for hover/pressed/focused/disabled.
   */
  hasBaseStyles = (): boolean => {
    const { editingTagValue } = this.state;

    if (editingTagValue) {
      // Edit mode: the tag's own project entries decide the base indicator
      // (unified by groupId, single-panel/legacy by section id — see
      // getEditingTagSectionEntries; KOA-15838: legacy tags were missed before).
      // Entries always carry their own id, so ignore the id guard.
      const entries = this.getEditingTagSectionEntries();
      for (const { section, entry } of entries) {
        if (sectionHasBaseStyle(entry as unknown as Record<string, unknown>, section as string, true)) return true;
      }
      // Fall through to DI check below — in edit mode, the DI styleObj reflects unsaved base edits.
    }

    // KOA-15838: read the CLEAN base. While in a non-none state the DI's base is
    // preview-corrupted with the active state's overlay (mirrored onto base for
    // live canvas preview), so reading it would light the "None" dot for values
    // that belong to the state, not the base. resolveCleanBaseSource routes to
    // statePreviewBackup (the state-entry snapshot) whenever we're in state mode,
    // and to the DI itself in none mode (where the base is already clean). A
    // genuine base override lives in the backup too, so the dot still stays lit
    // for it across state switches.
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    const baseSource: IKuikaStyle | undefined = resolveCleanBaseSource(
      this.props.selectedState,
      this.props.statePreviewBackup,
      selectedDi?.styleObj
    );
    return styleObjHasBaseStyle(baseSource);
  };

  handleStateChange = (state: StyleState) => {
    this.props.onStateChange(state, this.state.editingTagValue || undefined);
  };

  // The State tabs' wrapper. It sits outside the Form.Item that renders the tabs, so it outlives them.
  private stateTabsRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

  handleStateTabChange = (value: string): void => {
    const wrapper: HTMLDivElement | null = this.stateTabsRef.current;
    const hadFocus: boolean = wrapper !== null && wrapper.contains(document.activeElement);
    this.handleStateChange((value || "none") as StyleState);
    if (!hadFocus) return;
    // Switching state resets the styling form, and on a reset antd remounts everything under a
    // `shouldUpdate` Form.Item, these tabs included, so the radio that had focus is gone by the
    // next frame. Focus goes to the newly selected tab, and the arrow keys keep walking the states.
    requestAnimationFrame((): void => {
      this.stateTabsRef.current
        ?.querySelector<HTMLInputElement>(".rc-segmented-item-selected .rc-segmented-item-input")
        ?.focus();
    });
  };

  tagRender = (props: CustomTagProps) => {
    const { label, closable, onClose, value } = props;
    const unifiedList = this.getUnifiedStyleList();
    const styleItem = unifiedList.find((s) => s.id.toLowerCase() === String(value).toLowerCase());

    const handleOnClose = () => {
      if (styleItem) {
        this.removeFromComponent(styleItem);
      }
      if (onClose) {
        onClose();
      }
      this.setState({ activeStylingMenuTag: null });
    };

    const handleStylingMenuVisibleChange = (visible: boolean) => {
      this.setState({
        activeStylingMenuTag: visible ? value : null,
        isDropdownVisible: visible ? false : this.state.isDropdownVisible
      });
    };

    const friendlyName = this.getFriendlyName();

    const handleDelete = () => {
      if (styleItem) {
        this.setState({
          deleteArguments: {
            isDeleteStyleModalVisible: true,
            deletingStyleItem: styleItem,
            performStyleTagOnClose: onClose
          }
        });
      }
    };

    const stylingVersionMenu = (): KuikaDropdownMenuItemType[] => {
      const isDefault = this.isDefaultStyling(String(value));
      const menu: KuikaDropdownMenuItemType[] = [
        {
          key: "1",
          label: "Edit style",
          icon: <KuikaSvg width={16} height={16} name="settings" type="sidebar-icons" />,
          onClick: () => {
            if (styleItem) {
              this.editUnifiedStyle(styleItem);
            }
          }
        },
        {
          key: "3",
          label: `Remove from '${friendlyName}'`,
          icon: <KuikaSvg width={16} height={16} name="x" />,
          onClick: () => handleOnClose()
        },
        {
          key: "4",
          label: "Delete from project",
          icon: <KuikaSvg width={16} height={16} name="trash" />,
          onClick: handleDelete,
          danger: true,
          hidden: isDefault
        }
      ];
      return menu;
    };

    const isEditingThisTag = this.state.mode === EditMode.edit && this.state.editingTagValue === value;

    return (
      <StylingTag
        label={label}
        value={value}
        closable={closable}
        onClose={handleOnClose}
        isDefaultStyling={this.isDefaultStyling}
        isCurrentComponentDefaultStyling={this.isCurrentComponentDefaultStyling}
        stylingVersionMenu={stylingVersionMenu}
        mode={isEditingThisTag ? this.state.mode : undefined}
        isAnyEditing={this.state.mode === EditMode.edit}
        cancel={this.cancelEdit}
        saving={isEditingThisTag && this.state.isSaving}
        save={async (newValue?: string) => {
          const validName = this.validateStyleName(newValue, true);
          if (!validName) return;
          this.setState({ isSaving: true });
          try {
            await this.saveUnifiedStyle(validName);
          } finally {
            this.setState({ isSaving: false });
          }
        }}
        hasPendingChanges={isEditingThisTag && !!this.props.pendingTagStateStyles}
        isStylingMenuVisible={this.state.activeStylingMenuTag === value}
        setIsStylingMenuVisible={handleStylingMenuVisibleChange}
      />
    );
  };

  render() {
    const { searchText, isDropdownVisible, isEditingNewTag, creatingStyleName } = this.state;
    const isEditingTag: boolean = this.state.mode === EditMode.edit;
    const isCreatingTag: boolean = creatingStyleName !== null;
    // Only a real search draws the "chip to come". A rename also focuses an input inside the select,
    // and rc-select marks itself focused for that too.
    const isSearching: boolean = isDropdownVisible && !isEditingTag;
    const tagsClassName: string = isSearching
      ? "kuika-unified-styling-selector__tags kuika-unified-styling-selector__tags--searching"
      : "kuika-unified-styling-selector__tags";

    // The list's first row. With text typed it is the "Add" action, drawn as a row you press: a
    // tinted "+" tile, "Add", the name as the chip it will become, and the Enter hint. The kit's
    // header strip around it is the click target, so the whole width responds. With nothing typed
    // it is a quiet hint.
    const dropdownHeader: React.ReactNode = searchText ? (
      <span className="kuika-unified-styling-selector__create-row" data-testid="stylingTagCreateOption">
        <span className="kuika-unified-styling-selector__create-icon">
          <KuikaSvg name="plus" type="common" width={12} height={12} />
        </span>
        <span className="kuika-unified-styling-selector__create-label">Add</span>
        <span className="kuika-unified-styling-selector__create-name">{searchText}</span>
        <kbd className="kuika-unified-styling-selector__create-key" aria-hidden="true">
          ↵
        </kbd>
      </span>
    ) : (
      <span className="kuika-unified-styling-selector__dropdown-hint">Search styles or type a new name</span>
    );

    // State styles (hover/pressed/focused/disabled) compile to CSS pseudo-classes
    // (:hover/:active/:focus-visible) that only exist on web/desktop targets —
    // native mobile screens (Kotlin/Swift) have no such states. Hide the State
    // selector on a native mobile screen, mirroring the shadow-section gate in
    // styling-window.tsx (projectType !== Desktop && !isWebScreen).
    const isWebScreen: boolean = this.props.sd.getIsWebScreen();
    const isMobileScreen: boolean = this.props.sd.project.projectType !== EProjectType.Desktop && !isWebScreen;

    // The root screen (kuikaId "-1") has no interaction states — hover/pressed/
    // focused/disabled are meaningless on the screen container itself. Hide the
    // State selector when the screen is selected (mirrors the isScreenSelected
    // gate in styling-position-component.tsx).
    const isScreenSelected: boolean = this.props.sd.selectedKuikaIds[0] === "-1";

    // Report designer output is a static PDF — there are no interaction states
    // (hover/pressed/focused/disabled) to render, so the State selector has no
    // meaning there. Hide it, mirroring the mobile/native gate above.
    const isReportDesigner: boolean = this.props.sd.dashboardState === DashboardState.reportDesigner;

    // KOA-15838: while editing a style tag, the state styles being shown ARE the
    // tag's (token) values, so their "has styles" indicator must read BLUE (the
    // token colour, matching the per-section label `--has-value` style), not the
    // ORANGE "locally changed" dot used when editing a DI directly.
    const isTokenColor: boolean = this.state.mode === EditMode.edit;
    const indicatorClassName: string = isTokenColor
      ? "panel-change-indicator panel-change-indicator--token"
      : "panel-change-indicator";
    // A tab's label, followed by a dot when that state has styles. The dot keeps its room and the
    // name gives way: when five dotted tabs do not fit, the names end in an ellipsis and the tab's
    // title carries the full one. Only the selected tab's dot opens "Reset changes", as the dot on
    // the old dropdown's chosen value did. On the other tabs a click anywhere, the dot included,
    // picks the state.
    const makeStateLabel = (label: string, state: StyleState, hasStyles: boolean, isSelected: boolean) => (
      <span className="kuika-unified-styling-selector__state-option-label">
        {/* A plain span, not a typography component: those paint their own colour, which would beat
            the tab's own rules and leave the selected state a shade off every other tab bar in the
            panel. The tabs keep the kit's colours and take only their size from here. */}
        <span className="kuika-unified-styling-selector__state-name">{label}</span>
        {hasStyles &&
          (isSelected ? (
            // The tab is a <label>: a click inside it also "clicks" its radio, and Chrome moves
            // focus there, which the reset popover reads as focus leaving it and closes at once.
            // Cancelling the click's default in the capture phase stops that. The tab is already
            // selected, so there is nothing for the radio to do.
            //
            // Only for a click that really landed in this tab. The popover's own content is
            // portalled to <body>, but React sends its events up the COMPONENT tree — so an
            // unconditional cancel here also reached the popover's ✕, and Radix's close button
            // skips its own handler once the event is defaultPrevented. The panel was then left
            // with a popover nothing could dismiss.
            <span
              className="kuika-unified-styling-selector__state-reset"
              onClickCapture={(e: React.MouseEvent<HTMLSpanElement>): void => {
                const insideTab: boolean = e.currentTarget.contains(e.target as Node);
                if (insideTab) e.preventDefault();
              }}
            >
              <ResetChangesPopover onReset={() => this.props.onResetState?.(state)} section={state}>
                <span className={indicatorClassName} />
              </ResetChangesPopover>
            </span>
          ) : (
            <span className="kuika-unified-styling-selector__state-dot">
              <span className={indicatorClassName} />
            </span>
          ))}
      </span>
    );

    return (
      <div className="kuika-unified-styling-selector kuika-styling-selector" data-testid="stylingTagField">
        {/* Caption left, "+" right, as a Properties section header puts its add. The caption names
            the search box and opens it. Without the click handler, a click would focus a box that is
            zero-width at rest and leave the list closed. */}
        <div className="kuika-unified-styling-selector__header">
          <label htmlFor={STYLE_SEARCH_INPUT_ID} onClick={this.handleStyleCaptionClick}>
            <KuikaSmallMediumText>Style</KuikaSmallMediumText>
          </label>
          <ActionSectionAddButton
            ariaLabel="Add style"
            testId="stylingTagAddButton"
            onClick={this.openStyleSearch}
            disabled={isEditingTag || isCreatingTag}
            className="kuika-unified-styling-selector__add-button"
          />
        </div>
        {/* The chips ARE the field: it grows a row at a time as tags are added, with no fixed
            56/78px box, and takes the panel's full width. */}
        <div className="kuika-unified-styling-selector__tags-row" ref={this.tagsRowRef}>
          <Form.Item noStyle shouldUpdate>
            {() => {
              const currentSelectedIds = this.getSelectedStyleIds();
              const currentUnifiedList = this.getUnifiedStyleList();
              return (
                <KuikaSelectbox
                  data-testid="stylingTagSelector"
                  id={STYLE_SEARCH_INPUT_ID}
                  className={tagsClassName}
                  size="sm"
                  placeholder="No style"
                  mode="tags"
                  value={currentSelectedIds}
                  handleKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();

                      const currentSearchText = searchText;
                      if (currentSearchText) {
                        this.handleCreateNewStyle(currentSearchText);
                      }
                    } else if (e.key === "Backspace") {
                      this.handleBackspace(e);
                    }
                  }}
                  tagRender={this.tagRender}
                  searchable
                  onSearch={this.handleSearch}
                  searchValue={searchText}
                  tokenSeparators={[]}
                  preventTagCreation={true}
                  onChange={this.handleChange}
                  options={
                    currentUnifiedList.length > 0
                      ? currentUnifiedList
                          .sort((a, b) => CommonHelperFunctions.compareObjectWithField(a, b, "displayName"))
                          .filter(
                            (item) =>
                              !searchText ||
                              (item.displayName.toLowerCase().includes(searchText.toLowerCase()) &&
                                item.displayName.toLowerCase() !== searchText.toLowerCase())
                          )
                          .map((item) => ({
                            value: item.id.toLowerCase(),
                            label: item.displayName,
                            // Marks the rows that are real tags. Tags mode also lists the typed text
                            // as a row of its own, which repeats the "Add" row above it, and the
                            // overlay hides every row without this mark.
                            testId: "stylingTagOption"
                          }))
                      : []
                  }
                  filterValue={(value) => !this.isCurrentComponentDefaultStyling(String(value))}
                  dropdownHeader={dropdownHeader}
                  onDropdownHeaderClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    const currentSearchText = searchText;
                    if (currentSearchText) {
                      this.handleCreateNewStyle(currentSearchText);
                    }
                  }}
                  dropdownHeaderClassName={
                    searchText
                      ? "kuika-unified-styling-selector__dropdown-header kuika-unified-styling-selector__dropdown-header--create"
                      : "kuika-unified-styling-selector__dropdown-header"
                  }
                  visible={isDropdownVisible}
                  onVisibleChange={this.handleDropdownVisibleChange}
                  overlayClassName="kuika-unified-styling-selector__dropdown-overlay"
                  // A project's styles are tens, not thousands. The virtual list gives every row a
                  // fixed height, including the typed-text row the overlay hides, which then leaves
                  // a blank band above the matches.
                  virtual={false}
                />
              );
            }}
          </Form.Item>
        </div>
        {/* The create round trip takes seconds and the list has already closed. The field says it
            is working, and every way to add is refused until the new chip is in edit mode. */}
        {isCreatingTag && (
          <div
            className="kuika-unified-styling-selector__edit-note kuika-unified-styling-selector__edit-note--pending"
            data-testid="stylingTagCreatingNote"
          >
            <KuikaLoader
              size="sm"
              label="Creating style"
              className="kuika-unified-styling-selector__edit-note-loader"
            />
            <KuikaSmallMediumText>Creating style</KuikaSmallMediumText>
            <KuikaSmallText className="kuika-unified-styling-selector__edit-note-hint">
              {creatingStyleName}
            </KuikaSmallText>
          </div>
        )}
        {/* Creating a tag drops straight into the same rename-and-edit mode as "Edit style", so the
            field says which one it is: the chip alone reads as "renaming", not "every change from
            here lands on the style". */}
        {isEditingTag && !isCreatingTag && (
          <div className="kuika-unified-styling-selector__edit-note" data-testid="stylingTagEditNote">
            <KuikaSvg name={isEditingNewTag ? "plus" : "edit"} type="common" width={12} height={12} />
            <KuikaSmallMediumText>{isEditingNewTag ? "New style" : "Editing style"}</KuikaSmallMediumText>
            <KuikaSmallText className="kuika-unified-styling-selector__edit-note-hint">
              {isEditingNewTag ? "Set its values, then save" : "Applies wherever it's used"}
            </KuikaSmallText>
          </div>
        )}
        {/* Five tabs rather than a dropdown, so the state being edited is always in view. They size
            to their labels and share what is left, which fits the panel at its narrowest (286px). */}
        {!isMobileScreen && !isScreenSelected && !isReportDesigner && (
          <div className="kuika-unified-styling-selector__state" ref={this.stateTabsRef}>
            <KuikaSmallMediumText>State</KuikaSmallMediumText>
            <Form.Item noStyle shouldUpdate>
              {() => {
                // Compute indicators inside shouldUpdate so they react to every form value
                // change (no debounce wait). The currently selected state reads from the form;
                // the others fall back to the persisted data sources.
                const selectedState = this.props.selectedState;
                const formValues = this.props.form.getFieldsValue();
                const formHasValues = this.hasAnyFormSectionValue(formValues);

                const hasFor = (state: StyleState): boolean => {
                  if (state === selectedState) {
                    if (formHasValues) return true;
                    return state === "none" ? this.hasBaseStyles() : this.hasStateStyles(state);
                  }
                  return state === "none" ? this.hasBaseStyles() : this.hasStateStyles(state);
                };

                const stateOptions: KuikaTabOption[] = STYLE_STATE_TABS.map((tab: IStyleStateTab): KuikaTabOption => ({
                  value: tab.state,
                  dataTestId: `stylingState-${tab.state}`,
                  title: tab.label,
                  label: makeStateLabel(tab.label, tab.state, hasFor(tab.state), tab.state === selectedState)
                }));

                // A state other than None tints the chosen tab green, as the panel behind it is.
                const tabsClassName: string =
                  selectedState !== "none"
                    ? "kuika-unified-styling-selector__state-tabs kuika-unified-styling-selector__state-tabs--active"
                    : "kuika-unified-styling-selector__state-tabs";

                return (
                  <KuikaTabs
                    dataTestId="stylingStateSelector"
                    ariaLabel="State"
                    value={selectedState}
                    onChange={this.handleStateTabChange}
                    options={stateOptions}
                    className={tabsClassName}
                  />
                );
              }}
            </Form.Item>
          </div>
        )}
        <DeleteModal
          triggerModal={this.state.deleteArguments.isDeleteStyleModalVisible}
          itemName={this.state.deleteArguments.deletingStyleItem?.displayName || ""}
          deleteType="style"
          closeModal={() => {
            this.setState({
              deleteArguments: {
                isDeleteStyleModalVisible: false,
                deletingStyleItem: null,
                performStyleTagOnClose: undefined
              }
            });
          }}
          deleteItem={this.deleteFromProject}
        />
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.ChangePropertyValue ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeStyleValue ||
      changeReason === ESdChangeReason.ChangeStyleValueExternal ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock
    );
  };
}

const unifiedStylingSelectorComponent = withContext<IUnifiedStylingSelectorProps>(
  withSD<IUnifiedStylingSelectorProps>(UnifiedStylingSelector)
);
export { unifiedStylingSelectorComponent as UnifiedStylingSelector };
// The bare class, for tests that drive its handlers without the SD and context providers.
export { UnifiedStylingSelector as UnifiedStylingSelectorView };
